import {
  Box,
  Button,
  Container,
  CssBaseline,
  Link,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ResetPasswordRequest } from "../../app/models/requestModels/resetPasswordRequest";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import {
  resetPasswordByToken,
  resetUserPassword,
} from "../../app/features/user/userSlice";
import getAxiosError from "../../app/api/axiosError";
import { toast } from "react-toastify";
import Loader from "../../components/common/loader/Loader";
import InfoIcon from "@mui/icons-material/Info";
import { useEffect, useState } from "react";
import { Copyright } from "../login/Login";
import "./ResetPassword.css";
import { useSelector } from "react-redux";
import { Visibility, VisibilityOff } from "@mui/icons-material";

export function ResetPasswordFormModel(
  newPassword: string,
  confirmPassword: string,
  currentPassword?: string | null
): ResetPasswordRequest {
  return {
    newPassword: newPassword,
    confirmPassword: confirmPassword,
    currentPassword: currentPassword,
  };
}
interface ResetPasswordProps {
  forceResetPassword?: boolean;
  onDemandResetPassword?: boolean;
  byLinkResetPassword?: boolean;
}

const ResetPassword = ({
  forceResetPassword,
  onDemandResetPassword,
  byLinkResetPassword,
}: ResetPasswordProps) => {
  const dispatch = useAppDispatch();
  const isLoginLoading = useSelector(
    (state: RootState) => state.login.isLoginLoading
  );
  const urlParams = new URLSearchParams(window.location.search);
  const resetPasswordToken = urlParams.get("identity") as string;

  const [resetByToken, setResetByToken] = useState<boolean>(false);
  const isLoggedIn = localStorage.getItem("isLoggedIn");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    toast.error("CommandOps user credentials is expired.", {
      toastId: "reset-password-success",
    });
    if (resetPasswordToken) {
      setResetByToken(true);
    } else if (byLinkResetPassword && !resetPasswordToken) {
      handleBackToLogin();
    }
  }, [byLinkResetPassword, resetPasswordToken]);

  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .required("New Password is required")
      .matches(
        /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+}{":;'?/><.,[\]\-]).{8,}$/,
        "Password must contain at least one digit, one lowercase letter, one uppercase letter, one special character, and be at least 8 characters long"
      ),
    confirmPassword: Yup.string()
      .required("Confirm Password is required")
      .oneOf([Yup.ref("newPassword"), ""], "Passwords must match"),
    currentPassword: onDemandResetPassword
      ? Yup.string().required("Current Password is required")
      : Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const resetPasswordDetails = ResetPasswordFormModel(
          values.newPassword.trim(),
          values.confirmPassword.trim(),
          values.currentPassword.trim()
        );
        if (resetByToken) {
          const resultAction = await dispatch(
            resetPasswordByToken({
              resetPasswordRequest: resetPasswordDetails,
              token: resetPasswordToken,
            })
          ).unwrap();
          if (resultAction.isSuccess) {
            toast.success(resultAction.message!, {
              toastId: "reset-password-success",
            });
            localStorage.removeItem("isLoggedIn");
            backToLogin();
          }
          if (!resultAction.isSuccess) {
            toast.error(resultAction.message!, {
              toastId: "reset-password-success",
            });
          }
        } else {
          const resultAction = await dispatch(
            resetUserPassword(resetPasswordDetails)
          ).unwrap();

          if (resultAction.isSuccess) {
            toast.success(resultAction.message!, {
              toastId: "reset-password-success",
            });
            localStorage.removeItem("isLoggedIn");
            backToLogin();
          }
          if (!resultAction.isSuccess) {
            toast.error(resultAction.message!, {
              toastId: "reset-password-success",
            });
          }
        }
      } catch (error) {
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, { toastId: "reset-password-error" });
        backToLogin();
      }
    },
  });

  const backToLogin = () => {
    setTimeout(() => {
      window.history.replaceState({}, document.title, window.location.pathname);
      window.location.pathname = "/";
    }, 500);
  };
  const handleBackToLogin = () => {
    if (forceResetPassword) {
      localStorage.removeItem("isLoggedIn");
    }
    window.location.pathname = "/";
  };

  return (
    <>
      <div className="pageImageStyle">
        <Box
          sx={{
            margin: 0,
            padding: 0,
            textDecoration: "none",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "calc(10px + 2vmin)",
            position: "relative",
          }}
        >
          <Paper
            elevation={10}
            sx={{
              mt: 0,
              mb: 3,
            }}
          >
            <Container component="main" maxWidth="xs">
              <CssBaseline />
              <Box
                sx={{
                  marginTop: 3,
                  mb: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography variant="h4">Reset Password</Typography>
                <Box sx={{ mt: 2, mb: 4 }}>
                  <form onSubmit={formik.handleSubmit}>
                    {!resetByToken && onDemandResetPassword && (
                      <TextField
                        fullWidth
                        id="currentPassword"
                        name="currentPassword"
                        label="Current Password"
                        variant="standard"
                        size="medium"
                        type="password"
                        value={formik.values.currentPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          onDemandResetPassword &&
                          formik.touched.currentPassword &&
                          Boolean(formik.errors.currentPassword)
                        }
                        helperText={
                          onDemandResetPassword &&
                          formik.touched.currentPassword &&
                          formik.errors.currentPassword
                        }
                      />
                    )}
                    <TextField
                      fullWidth
                      id="newPassword"
                      name="newPassword"
                      label="New Password"
                      variant="standard"
                      size="medium"
                      type={showNewPassword ? "text" : "password"}
                      sx={{ mt: 1 }}
                      value={formik.values.newPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.newPassword &&
                        Boolean(formik.errors.newPassword)
                      }
                      helperText={
                        formik.touched.newPassword && formik.errors.newPassword
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                              edge="end"
                            >
                              {showNewPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      id="confirmPassword"
                      name="confirmPassword"
                      label="Confirm Password"
                      variant="standard"
                      size="medium"
                      type={showConfirmPassword ? "text" : "password"}
                      sx={{ mt: 1 }}
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.confirmPassword &&
                        Boolean(formik.errors.confirmPassword)
                      }
                      helperText={
                        formik.touched.confirmPassword &&
                        formik.errors.confirmPassword
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      sx={{ mt: 2, textTransform: "none", fontSize: 14 }}
                      variant="contained"
                      size="medium"
                      fullWidth
                      type="submit"
                      disabled={!formik.isValid || formik.isSubmitting}
                    >
                      Reset Password
                    </Button>
                  </form>
                </Box>
                <Box display="flex" alignItems="center">
                  <InfoIcon sx={{ marginRight: 1 }} />
                  <Typography color={"#0d3b15"} fontWeight={600}>
                    Password must contain at least a digit, a lowercase, an
                    uppercase letter, a special character, and be at least 8
                    characters long.
                    <br />
                    Allowed Special Chars:{" "}
                    <b>!@#$%^&*()_+&#125; &#123;":;'?/&gt;&lt;.,[]\- </b>
                  </Typography>
                </Box>
                {!isLoginLoading && (forceResetPassword || !isLoggedIn) && (
                  <Link
                    sx={{ cursor: "pointer" }}
                    onClick={handleBackToLogin}
                    variant="body1"
                  >
                    Return To Login
                  </Link>
                )}
                {!isLoginLoading && onDemandResetPassword && isLoggedIn && (
                  <Link
                    sx={{ cursor: "pointer" }}
                    onClick={() => (window.location.pathname = "/home")}
                    variant="body1"
                  >
                    Return To Home
                  </Link>
                )}
              </Box>
              <Copyright sx={{ mt: 3, mb: 4 }} />
            </Container>
          </Paper>
        </Box>
        <Loader isLoading={formik.isSubmitting} />
      </div>
    </>
  );
};

export default ResetPassword;
